import React from "react";
import { SEO } from "../../seo/SEO";
import styled from "styled-components";
import { useTypedResource } from "../../i18n/languagehandler";

const Hero = styled.section`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
`;

const HeroContainer = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
`;

const HeroBgWrapper = styled.div`
  position: relative;
  border-radius: 0;
  overflow: hidden;
  height: calc(100vh - 64px);
  min-height: 420px;
  background: var(--bg);
  @media (max-width: 900px) {
    height: 60vh;
    min-height: 360px;
  }
`;

const HeroBg = styled.img`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 50%;
  height: 100%;
  object-fit: cover;
  filter: saturate(1.05);
  object-position: center center;
  z-index: 1;
  background: var(--bg);
  @media (max-width: 900px) {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
  }
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: none;
  z-index: 2;
  pointer-events: none;
  @media (max-width: 900px) {
    display: block;
    background: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0.2) 0%,
      rgba(0, 0, 0, 0.45) 40%,
      rgba(0, 0, 0, 0.7) 100%
    );
  }
`;

const HeroContent = styled.div`
  position: absolute;
  top: 50%;
  left: auto;
  transform: translateY(-50%);
  max-width: 640px;
  right: calc(50% + 24px);
  padding: 0 20px 0 0;
  color: var(--text);
  font-family: inherit;
  word-wrap: normal;
  overflow-wrap: normal;
  hyphens: manual;
  -webkit-hyphens: manual;
  -ms-hyphens: manual;
  z-index: 3;
  @media (max-width: 900px) {
    top: auto;
    bottom: 8vh;
    left: 16px;
    right: 16px;
    transform: none;
    max-width: none;
    padding: 0;
    color: #fff;
  }
`;

const HeroTitle = styled.h1`
  color: var(--text);
  font-size: 64px;
  margin: 0 0 16px 0;
  line-height: 1.2;
  text-transform: uppercase;
  text-shadow: none;
  font-family: inherit;
  @media (max-width: 900px) {
    font-size: 32px;
    color: #fff;
  }
`;

const HeroSubtitle = styled.h2`
  color: var(--text);
  font-size: 28px;
  margin: 0 0 16px 0;
  font-family: inherit;
  white-space: pre-line;
  @media (max-width: 900px) {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.95);
  }
`;

const HeroExpertise = styled.p`
  color: var(--text);
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 16px 0;
  font-family: inherit;
  @media (max-width: 900px) {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.95);
  }
`;

const HeroCTA = styled.a`
  background: var(--primary);
  color: #fff;
  text-decoration: none;
  padding: 14px 28px;
  border-radius: 12px;
  font-weight: 500;
  display: inline-block;
  transition: all 0.2s ease;
  box-shadow: 0 12px 32px rgba(55, 90, 127, 0.35);
  &:hover {
    background: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 12px 32px rgba(55, 90, 127, 0.5);
  }
  font-family: inherit;
  @media (max-width: 900px) {
    padding: 12px 20px;
    border-radius: 10px;
  }
`;

const Subtitle = styled.h2`
  color: var(--primary);
  font-size: 24px;
  margin: 0 0 16px 0;
`;

// Removed old HeroImage (not used with new full-width hero)

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

// Lectures preview-specific styles to match Lectures page
const LectureCardImage = styled.img`
  width: 100%;
  height: 280px;
  object-fit: cover;
`;

const CardTeaser = styled.p`
  color: var(--primary);
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  margin: 0 0 12px 0;
`;

const LectureButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: auto;
`;

const CardTitle = styled.h3`
  color: var(--text);
  font-size: 20px;
  margin: 0 0 12px 0;
  line-height: 1.3;
`;

const CardDescription = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 20px 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
`;

const PrimaryButton = styled.a`
  background: var(--primary);
  color: #fff;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-block;
  &:hover {
    background: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(55, 90, 127, 0.3);
  }
`;

const PodcastsSection = styled.section`
  max-width: 1200px;
  margin: 60px auto 0;
  padding: 0 20px;
`;

const VideosSection = styled.section`
  max-width: 1200px;
  margin: 60px auto 0;
  padding: 0 20px;
`;

const LecturesSection = styled.section`
  max-width: 1200px;
  margin: 60px auto 0;
  padding: 0 20px;
`;

const BodyText = styled.p`
  color: var(--text-secondary);
  font-size: 18px;
  line-height: 1.6;
  margin: 0 0 20px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-top: 30px;
`;

export const Frontpage: React.FC = () => {
  const res = useTypedResource<{
    heroTitle: string;
    heroNameTitle: string;
    heroSubtitle: string;
    heroDescription: string;
    heroExpertise: string;
    cta: string;
    videosTitle: string;
    videosIntro: string;
    podcastsTitle: string;
    podcastsIntro: string;
    video1Title: string;
    video1Description: string;
    video1Url: string;
    video2Title: string;
    video2Description: string;
    video2Url: string;
    video3Title: string;
    video3Description: string;
    video3Url: string;
    video4Title: string;
    video4Description: string;
    video4Url: string;
    video5Title: string;
    video5Description: string;
    video5Url: string;
    podcast1Title: string;
    podcast1Description: string;
    podcast1Url: string;
    podcast2Title: string;
    podcast2Description: string;
    podcast2Url: string;
    podcast3Title: string;
    podcast3Description: string;
    podcast3Url: string;
    podcast4Title: string;
    podcast4Description: string;
    podcast4Url: string;
    podcast5Title: string;
    podcast5Description: string;
    podcast5Url: string;
    podcast6Title: string;
    podcast6Description: string;
    podcast6Url: string;
    watchButton: string;
    learnMoreButton: string;
  }>("home");
  const mediaRes = useTypedResource<{
    video1Title: string;
    video1Description: string;
    video1Url: string;
    video2Title: string;
    video2Description: string;
    video2Url: string;
    video3Title: string;
    video3Description: string;
    video3Url: string;
    video4Title: string;
    video4Description: string;
    video4Url: string;
    video5Title: string;
    video5Description: string;
    video5Url: string;
    video6Title: string;
    video6Description: string;
    video6Url: string;
    video7Title: string;
    video7Description: string;
    video7Url: string;
    video8Title: string;
    video8Description: string;
    video8Url: string;
    video9Title: string;
    video9Description: string;
    video9Url: string;
    video10Title: string;
    video10Description: string;
    video10Url: string;
    video11Title: string;
    video11Description: string;
    video11Url: string;
    video12Title: string;
    video12Description: string;
    video12Url: string;
    video13Title: string;
    video13Description: string;
    video13Url: string;
    video14Title: string;
    video14Description: string;
    video14Url: string;
    video15Title: string;
    video15Description: string;
    video15Url: string;
    video16Title: string;
    video16Description: string;
    video16Url: string;
    video17Title: string;
    video17Description: string;
    video17Url: string;
    video18Title: string;
    video18Description: string;
    video18Url: string;
    video19Title: string;
    video19Description: string;
    video19Url: string;
    video20Title: string;
    video20Description: string;
    video20Url: string;
    podcast1Title: string;
    podcast1Description: string;
    podcast1Url: string;
    podcast2Title: string;
    podcast2Description: string;
    podcast2Url: string;
    podcast3Title: string;
    podcast3Description: string;
    podcast3Url: string;
    podcast4Title: string;
    podcast4Description: string;
    podcast4Url: string;
    podcast5Title: string;
    podcast5Description: string;
    podcast5Url: string;
    podcast6Title: string;
    podcast6Description: string;
    podcast6Url: string;
    podcast7Title: string;
    podcast7Description: string;
    podcast7Url: string;
    podcast8Title: string;
    podcast8Description: string;
    podcast8Url: string;
    podcast9Title: string;
    podcast9Description: string;
    podcast9Url: string;
    podcast10Title: string;
    podcast10Description: string;
    podcast10Url: string;
    podcast11Title: string;
    podcast11Description: string;
    podcast11Url: string;
  }>("media");
  const lecturesRes = useTypedResource<{
    title: string;
    intro: string;
    card1Title: string;
    card1Teaser: string;
    card2Title: string;
    card2Teaser: string;
    card3Title: string;
    card3Teaser: string;
    card4Title: string;
    card4Teaser: string;
    card5Title: string;
    card5Teaser: string;
    card6Title: string;
    card6Teaser: string;
  }>("lectures");

  const videos = [
    {
      title: mediaRes.video1Title,
      description: mediaRes.video1Description,
      url: mediaRes.video1Url,
      image: "/assets/Media/Picture 1.jpg",
    },
    {
      title: mediaRes.video2Title,
      description: mediaRes.video2Description,
      url: mediaRes.video2Url,
      image: "/assets/Media/Picture 2.jpg",
    },
    {
      title: mediaRes.video15Title,
      description: mediaRes.video15Description,
      url: mediaRes.video15Url,
      image: "/assets/Media/Picture 15.jpg",
    },
    {
      title: mediaRes.video18Title,
      description: mediaRes.video18Description,
      url: mediaRes.video18Url,
      image: "/assets/Media/Picture 18.jpg",
    },
    {
      title: mediaRes.video19Title,
      description: mediaRes.video19Description,
      url: mediaRes.video19Url,
      image: "/assets/Media/Picture 19.jpg",
    },
    {
      title: mediaRes.video20Title,
      description: mediaRes.video20Description,
      url: mediaRes.video20Url,
      image: "/assets/Media/Picture 20.jpg",
    },
    {
      title: mediaRes.video8Title,
      description: mediaRes.video8Description,
      url: mediaRes.video8Url,
      image: "/assets/Media/Picture 8.jpg",
    },
    {
      title: mediaRes.video9Title,
      description: mediaRes.video9Description,
      url: mediaRes.video9Url,
      image: "/assets/Media/Picture 9.jpg",
    },
    {
      title: mediaRes.video10Title,
      description: mediaRes.video10Description,
      url: mediaRes.video10Url,
      image: "/assets/Media/Picture 10.jpg",
    },
    {
      title: mediaRes.video11Title,
      description: mediaRes.video11Description,
      url: mediaRes.video11Url,
      image: "/assets/Media/Picture 11.jpg",
    },
    {
      title: mediaRes.video12Title,
      description: mediaRes.video12Description,
      url: mediaRes.video12Url,
      image: "/assets/Media/Picture 12.jpg",
    },
    {
      title: mediaRes.video13Title,
      description: mediaRes.video13Description,
      url: mediaRes.video13Url,
      image: "/assets/Media/Picture 13.jpg",
    },
    {
      title: mediaRes.video15Title,
      description: mediaRes.video15Description,
      url: mediaRes.video15Url,
      image: "/assets/Media/Picture 15.jpg",
    },
    {
      title: mediaRes.video16Title,
      description: mediaRes.video16Description,
      url: mediaRes.video16Url,
      image: "/assets/Media/Picture 16.jpg",
    },
    {
      title: mediaRes.video17Title,
      description: mediaRes.video17Description,
      url: mediaRes.video17Url,
      image: "/assets/Media/Picture 17.jpg",
    },
  ];

  const podcasts = [
    {
      title: mediaRes.podcast1Title,
      description: mediaRes.podcast1Description,
      image: "/assets/Podcasts/Picture 1.jpg",
      url: mediaRes.podcast1Url,
    },
    {
      title: mediaRes.podcast2Title,
      description: mediaRes.podcast2Description,
      image: "/assets/Podcasts/Picture 2 Podcast.jpg",
      url: mediaRes.podcast2Url,
    },
    {
      title: mediaRes.podcast4Title,
      description: mediaRes.podcast4Description,
      image: "/assets/Podcasts/Picture 4_Podcast sybersundhed.jpg",
      url: mediaRes.podcast4Url,
    },
    {
      title: mediaRes.podcast5Title,
      description: mediaRes.podcast5Description,
      image: "/assets/Podcasts/Picture 5_Podcast Signe.jpg",
      url: mediaRes.podcast5Url,
    },
    {
      title: mediaRes.podcast6Title,
      description: mediaRes.podcast6Description,
      image: "/assets/Podcasts/Picture 6_Podcast Brainstorm kopi.jpg",
      url: mediaRes.podcast6Url,
    },
    {
      title: mediaRes.podcast7Title,
      description: mediaRes.podcast7Description,
      image: "/assets/Podcasts/Picture 7_Podcast_D.jpg",
      url: mediaRes.podcast7Url,
    },
    {
      title: mediaRes.podcast8Title,
      description: mediaRes.podcast8Description,
      image: "/assets/Podcasts/Picture 8_Podcast_Psychologos.jpg",
      url: mediaRes.podcast8Url,
    },
    {
      title: mediaRes.podcast9Title,
      description: mediaRes.podcast9Description,
      image: "/assets/Podcasts/Picture 9_WDR.jpg",
      url: mediaRes.podcast9Url,
    },
  ];

  const lecturesPreview = [
    {
      title: lecturesRes.card1Title,
      teaser: lecturesRes.card1Teaser,
      image: "/assets/Lectures/Lecture 1 picture.png",
    },
    {
      title: lecturesRes.card4Title,
      teaser: lecturesRes.card4Teaser,
      image: "/assets/Lectures/Lecture 4.jpeg",
    },

    {
      title: lecturesRes.card3Title,
      teaser: lecturesRes.card3Teaser,
      image: "/assets/Lectures/Lecture 3.png",
    },
    {
      title: lecturesRes.card2Title,
      teaser: lecturesRes.card2Teaser,
      image: "/assets/Lectures/Lecture 2 picture.jpg",
    },
    {
      title: lecturesRes.card5Title,
      teaser: lecturesRes.card5Teaser,
      image: "/assets/Lectures/Lecture 5.png",
    },
    {
      title: lecturesRes.card6Title,
      teaser: lecturesRes.card6Teaser,
      image: "/assets/Lectures/Lecture 6.png",
    },
  ];

  return (
    <main>
      <SEO
        title={res.heroTitle}
        description={res.heroSubtitle}
        path="/"
        image={"/assets/Frontpage/Photo Frontpage.jpeg"}
      />

      <Hero>
        <HeroContainer>
          <HeroBgWrapper>
            <HeroBg
              src={
                process.env.PUBLIC_URL +
                "/assets/Frontpage/Photo Frontpage.jpeg"
              }
              alt="Aida Bikic"
            />
            <HeroOverlay />
            <HeroContent>
              <HeroTitle>{res.heroTitle}</HeroTitle>
              <HeroExpertise>{res.heroNameTitle}</HeroExpertise>
              <HeroSubtitle>{res.heroSubtitle}</HeroSubtitle>
              <HeroExpertise>{res.heroDescription}</HeroExpertise>
              <HeroExpertise>{res.heroExpertise}</HeroExpertise>
              <HeroCTA href="/about">{res.cta}</HeroCTA>
            </HeroContent>
          </HeroBgWrapper>
        </HeroContainer>
      </Hero>

      <LecturesSection>
        <Subtitle>{lecturesRes.title}</Subtitle>
        <Grid>
          {lecturesPreview.map((lecture, index) => (
            <Card key={index}>
              <LectureCardImage
                src={process.env.PUBLIC_URL + lecture.image}
                alt={lecture.title}
              />
              <CardContent style={{ textAlign: "left" }}>
                <CardTitle>{lecture.title}</CardTitle>
                <CardTeaser>{lecture.teaser}</CardTeaser>
                <LectureButtonGroup>
                  <PrimaryButton href="/lectures">
                    {res.learnMoreButton}
                  </PrimaryButton>
                </LectureButtonGroup>
              </CardContent>
            </Card>
          ))}
        </Grid>
      </LecturesSection>

      <PodcastsSection>
        <Subtitle>{res.podcastsTitle}</Subtitle>
        <BodyText>{res.podcastsIntro}</BodyText>
        <Grid>
          {podcasts.map((podcast, index) => (
            <Card key={index}>
              <CardImage
                src={process.env.PUBLIC_URL + podcast.image}
                alt={podcast.title}
              />
              <CardContent>
                <CardTitle>{podcast.title}</CardTitle>
                <CardDescription>{podcast.description}</CardDescription>
                <ButtonGroup>
                  <PrimaryButton
                    href={podcast.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {res.watchButton}
                  </PrimaryButton>
                </ButtonGroup>
              </CardContent>
            </Card>
          ))}
        </Grid>
      </PodcastsSection>

      <VideosSection>
        <Subtitle>{res.videosTitle}</Subtitle>
        <BodyText>{res.videosIntro}</BodyText>
        <Grid>
          {videos.map((video, index) => (
            <Card key={index}>
              <CardImage
                src={process.env.PUBLIC_URL + video.image}
                alt={video.title}
              />
              <CardContent>
                <CardTitle>{video.title}</CardTitle>
                <CardDescription>{video.description}</CardDescription>
                <ButtonGroup>
                  <PrimaryButton
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {res.watchButton}
                  </PrimaryButton>
                </ButtonGroup>
              </CardContent>
            </Card>
          ))}
        </Grid>
      </VideosSection>
    </main>
  );
};
